import React, { useState, useEffect } from 'react';
import { AlertTriangle, ArrowRight, Clock, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import {
  fetchContracts,
  compareDeadlineClauses,
  type ContractFromAPI,
  type DeadlineComparisonPair,
} from '../../services/api';

interface DeadlineComparisonPanelProps {
  contractId: string;
}

const urgencyConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  high: { label: 'High', color: 'text-red-700', bgColor: 'bg-red-100' },
  medium: { label: 'Medium', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  low: { label: 'Low', color: 'text-blue-700', bgColor: 'bg-blue-100' },
};

function formatDays(days: number | null): string {
  if (days === null) return 'N/A';
  if (days === 0) return 'Immediate';
  if (days < 1) return `${Math.round(days * 24)}h`;
  if (days < 7) return `${Math.round(days)}d`;
  if (days < 30) return `${Math.round(days / 7)}w`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}

export const DeadlineComparisonPanel: React.FC<DeadlineComparisonPanelProps> = ({
  contractId,
}) => {
  const [contracts, setContracts] = useState<ContractFromAPI[]>([]);
  const [targetContractId, setTargetContractId] = useState<string>('');
  const [pairs, setPairs] = useState<DeadlineComparisonPair[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalSource, setTotalSource] = useState(0);
  const [totalTarget, setTotalTarget] = useState(0);

  // Load available contracts
  useEffect(() => {
    const loadContracts = async () => {
      setIsLoadingContracts(true);
      try {
        const result = await fetchContracts({ limit: 100 });
        // Filter out the current contract
        setContracts(result.contracts.filter((c) => c.id !== contractId));
      } catch (err) {
        console.error('[DeadlineComparison] Failed to load contracts:', err);
      } finally {
        setIsLoadingContracts(false);
      }
    };
    loadContracts();
  }, [contractId]);

  const handleCompare = async () => {
    if (!targetContractId) return;

    setIsLoading(true);
    setError(null);
    setPairs([]);

    try {
      const result = await compareDeadlineClauses(contractId, targetContractId);
      setPairs(result.pairs);
      setTotalSource(result.totalSourceFindings);
      setTotalTarget(result.totalTargetFindings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comparison failed');
    } finally {
      setIsLoading(false);
    }
  };

  const sourceContract = contracts.find((c) => c.id === contractId);
  const targetContract = contracts.find((c) => c.id === targetContractId);

  return (
    <div className="space-y-6" data-testid="deadline-comparison-panel">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Compare Deadline Requirements</h3>
        <p className="text-sm text-gray-500">
          Select a contract to compare against. Deadline findings are matched by section topic and category
          to highlight differences in time requirements.
        </p>
      </div>

      {/* Contract selector */}
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Compare with contract:
          </label>
          <select
            value={targetContractId}
            onChange={(e) => setTargetContractId(e.target.value)}
            disabled={isLoadingContracts}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="target-contract-select"
          >
            <option value="">
              {isLoadingContracts ? 'Loading contracts...' : 'Select a contract...'}
            </option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.state})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleCompare}
          disabled={!targetContractId || isLoading}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 text-sm font-medium"
          data-testid="run-comparison-button"
        >
          {isLoading ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Comparing...
            </>
          ) : (
            <>
              <ArrowRight className="h-4 w-4 mr-2" />
              Compare
            </>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Summary */}
      {pairs.length > 0 && (
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>{pairs.length} matched deadline pair{pairs.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-400">|</span>
          <span>Source: {totalSource} findings</span>
          <span>Target: {totalTarget} findings</span>
        </div>
      )}

      {/* No results message */}
      {!isLoading && pairs.length === 0 && targetContractId && totalSource === 0 && (
        <div className="text-center py-8 text-gray-400">
          Run the deadline analysis on both contracts first, then compare.
        </div>
      )}

      {/* Comparison pairs */}
      {pairs.length > 0 && (
        <div className="space-y-4" data-testid="comparison-pairs">
          {pairs.map((pair, idx) => {
            const sourceUrgency = urgencyConfig[pair.sourceUrgency] || urgencyConfig.low;
            const targetUrgency = urgencyConfig[pair.targetUrgency] || urgencyConfig.low;

            let diffIcon: React.ReactNode = <Minus className="h-4 w-4 text-gray-400" />;
            let diffLabel = 'Same';
            let diffColor = 'text-gray-600';

            if (pair.daysDifference !== null) {
              if (pair.daysDifference < 0) {
                diffIcon = <ArrowDownRight className="h-4 w-4 text-red-600" />;
                diffLabel = `Target is ${Math.abs(Math.round(pair.daysDifference))} days tighter`;
                diffColor = 'text-red-600';
              } else if (pair.daysDifference > 0) {
                diffIcon = <ArrowUpRight className="h-4 w-4 text-green-600" />;
                diffLabel = `Target allows ${Math.round(pair.daysDifference)} more days`;
                diffColor = 'text-green-600';
              }
            }

            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden"
                data-testid={`comparison-pair-${idx}`}
              >
                {/* Section header with difference indicator */}
                <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    {pair.sourceSectionTitle || `Art. ${pair.sourceArticleNumber} / Sec. ${pair.sourceSectionNumber}`}
                  </span>
                  <div className={`flex items-center gap-1 text-xs font-medium ${diffColor}`}>
                    {diffIcon}
                    {diffLabel}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                  {/* Source clause */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Source</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${sourceUrgency.bgColor} ${sourceUrgency.color}`}>
                        {sourceUrgency.label}
                      </span>
                      {pair.sourceNormalizedDays !== null && (
                        <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                          <Clock className="h-3 w-3" />
                          {formatDays(pair.sourceNormalizedDays)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-6">
                      {pair.sourceClauseText}
                    </p>
                  </div>

                  {/* Target clause */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Target</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${targetUrgency.bgColor} ${targetUrgency.color}`}>
                        {targetUrgency.label}
                      </span>
                      {pair.targetNormalizedDays !== null && (
                        <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                          <Clock className="h-3 w-3" />
                          {formatDays(pair.targetNormalizedDays)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-6">
                      {pair.targetClauseText}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
